import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { toast } from '@/components/ui/toaster'
import { Plus, Mail, MoreHorizontal, Loader2, Shield, User as UserIcon, Trash2, Copy } from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'

interface User { id: string; email: string; name: string; avatarUrl: string | null; role: string }
interface Invitation { id: string; email: string; role: string; token: string; expiresAt: number }

const ROLES = [
    { value: 'admin', label: 'Admin', icon: Shield },
    { value: 'producer', label: 'Producer', icon: UserIcon },
    { value: 'writer', label: 'Writer', icon: UserIcon },
    { value: 'member', label: 'Member', icon: UserIcon },
]

export default function TeamPage() {
    const queryClient = useQueryClient()
    const { user: currentUser, tenant } = useAuthStore()
    const [isInviteOpen, setIsInviteOpen] = useState(false)
    const [inviteEmail, setInviteEmail] = useState('')
    const [inviteRole, setInviteRole] = useState('member')

    const { data: usersData, isLoading } = useQuery({ queryKey: ['users'], queryFn: () => api.get<User[]>('/users') })
    const { data: invitationsData } = useQuery({ queryKey: ['invitations'], queryFn: () => api.get<Invitation[]>('/users/invitations') })

    const inviteMutation = useMutation({
        mutationFn: (data: { email: string; role: string }) => api.post('/users/invite', data),
        onSuccess: (response: any) => {
            queryClient.invalidateQueries({ queryKey: ['invitations'] })
            setIsInviteOpen(false)
            navigator.clipboard.writeText(response.data.inviteUrl)
            toast({ title: 'Invitation sent', description: 'Link copied to clipboard' })
        },
    })

    const removeUserMutation = useMutation({
        mutationFn: (userId: string) => api.delete(`/users/${userId}`),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['users'] }); toast({ title: 'User removed' }) },
    })

    const users = usersData?.data || []
    const invitations = invitationsData?.data || []
    const isAdmin = currentUser?.role === 'admin'

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Team</h1>
                    <p className="text-muted-foreground">Manage your {tenant?.name} team</p>
                </div>
                {isAdmin && (
                    <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
                        <DialogTrigger asChild><Button><Plus className="mr-2 h-4 w-4" />Invite</Button></DialogTrigger>
                        <DialogContent>
                            <form onSubmit={(e) => { e.preventDefault(); inviteMutation.mutate({ email: inviteEmail, role: inviteRole }) }}>
                                <DialogHeader><DialogTitle>Invite Member</DialogTitle></DialogHeader>
                                <div className="space-y-4 py-4">
                                    <div><Label>Email</Label><Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required /></div>
                                    <div><Label>Role</Label>
                                        <div className="flex gap-2 mt-2">{ROLES.map(r => (
                                            <Button key={r.value} type="button" variant={inviteRole === r.value ? 'default' : 'outline'} size="sm" onClick={() => setInviteRole(r.value)}>{r.label}</Button>
                                        ))}</div>
                                    </div>
                                </div>
                                <DialogFooter><Button type="submit" disabled={inviteMutation.isPending}>{inviteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Send</Button></DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <div className="border rounded-lg divide-y">
                {isLoading ? <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div> : users.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-4">
                        <div className="flex items-center gap-3">
                            <Avatar><AvatarImage src={user.avatarUrl || undefined} /><AvatarFallback>{user.name.charAt(0)}</AvatarFallback></Avatar>
                            <div><p className="font-medium">{user.name} {user.id === currentUser?.id && <span className="text-xs bg-primary/10 text-primary px-1.5 rounded">You</span>}</p><p className="text-sm text-muted-foreground">{user.email}</p></div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm px-2 py-1 bg-accent rounded-full capitalize">{user.role}</span>
                            {isAdmin && user.id !== currentUser?.id && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                                    <DropdownMenuContent><DropdownMenuItem className="text-destructive" onClick={() => removeUserMutation.mutate(user.id)}><Trash2 className="mr-2 h-4 w-4" />Remove</DropdownMenuItem></DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {invitations.length > 0 && (
                <div>
                    <h2 className="text-lg font-medium mb-4">Pending Invitations</h2>
                    <div className="border rounded-lg divide-y">
                        {invitations.map((inv) => (
                            <div key={inv.id} className="flex items-center justify-between p-4">
                                <div className="flex items-center gap-3"><Mail className="h-5 w-5 text-muted-foreground" /><div><p>{inv.email}</p><p className="text-sm text-muted-foreground">Invited as {inv.role}</p></div></div>
                                <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/invite/${inv.token}`); toast({ title: 'Copied' }) }}><Copy className="mr-2 h-4 w-4" />Copy</Button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}
